import * as React from 'react';
const { useState, useRef, useCallback, useEffect } = React;

import {
	Root,
	ControlsLayout,
	ButtonStyled,
	SliderLayout,
	Slider,
	SliderBar,
} from './styled';

type Value = string | number;

export type TabPickerDataSourceItem = {value: Value; name: string;}

type TabPickerProps = {
	value: Value;
	dataSource: Array<TabPickerDataSourceItem>;
	fullWidth?: boolean;
	negativeOffset?: number;
	onChange: (e: React.SyntheticEvent<any>, value: Value, item: TabPickerDataSourceItem) => void;
}

const TabPicker: React.SFC<TabPickerProps> = (props) => {
	const {
		value,
		dataSource,
		fullWidth,
		negativeOffset,
		onChange,
	} = props;
	const controlsRef = useRef<HTMLElement>(null);
	const [target, setTarget] = useState<HTMLElement>(null);
	const [sliderBarWidth, setSliderBarWidth] = useState<number>(0);
	const [sliderOffset, setSliderOffset] = useState<number>(0);

	useEffect(() => {
		const idx = dataSource.findIndex(s => s.value === value);
		const DOMElement = controlsRef && controlsRef.current && (controlsRef.current as HTMLElement).children[idx] as HTMLElement;

		if (DOMElement) {
			setTarget(DOMElement);
			setSliderBarWidth(DOMElement.offsetWidth);
			setSliderOffset(DOMElement.offsetLeft);
		}

		const handleResize = () => {
			if (!target) return;
			setSliderBarWidth(target.offsetWidth);
			setSliderOffset(target.offsetLeft);
		};	

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [target, value]);

	const handleClick = useCallback((item: TabPickerDataSourceItem) => (e: React.SyntheticEvent<any>) => {
		e.stopPropagation();
		onChange(e, item.value, item);
	}, []);

	return (
		<Root>
			<ControlsLayout
				ref={controlsRef as any}
				fullWidth={fullWidth}>
				{
					dataSource.map((source, idx) => {
						return (
							<ButtonStyled
								key={idx}
								data-tab-button='true'
								isActive={source.value === value}
								fullWidth={fullWidth}
								onClick={handleClick(source)}>
								{source.name}
							</ButtonStyled>
						);
					})
				}
			</ControlsLayout>
			<SliderLayout>
				<Slider negativeOffset={negativeOffset}>
					<SliderBar
						width={sliderBarWidth}
						offset={sliderOffset + negativeOffset} />
				</Slider>
			</SliderLayout>
		</Root>
	);
}

TabPicker.displayName = 'TabPicker';
TabPicker.defaultProps = {
	dataSource: [],
	negativeOffset: 0
};

export default TabPicker;
